(() => {
  'use strict';

  const ROOT_SELECTOR = '[data-deco-music-player]';
  const STORAGE_KEY = 'deco-music-player:v1';
  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
  const formatTime = (seconds) => {
    if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
    const whole = Math.floor(seconds);
    const minutes = Math.floor(whole / 60);
    const remaining = String(whole % 60).padStart(2, '0');
    return `${minutes}:${remaining}`;
  };
  const escapeHTML = (value = '') => String(value).replace(/[&<>"']/g, (ch) => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#039;'}[ch]));

  class DecoMusicPlayer {
    constructor(root) {
      this.root = root;
      this.data = this.readData();
      this.tracks = Array.isArray(this.data.tracks) ? this.data.tracks.filter((track) => track && track.audio) : [];
      this.settings = {
        remember: Boolean(this.data.settings?.remember),
        autoplayNext: this.data.settings?.autoplayNext !== false,
        defaultVolume: clamp(Number(this.data.settings?.defaultVolume ?? 0.8), 0, 1)
      };
      this.audio = root.querySelector('.dmp-audio');
      this.panel = root.querySelector('[data-dmp-panel]');
      this.scrim = root.querySelector('[data-dmp-scrim]');
      this.openButton = root.querySelector('[data-dmp-open]');
      this.closeButton = root.querySelector('[data-dmp-close]');
      this.toggleButtons = [...root.querySelectorAll('[data-dmp-toggle]')];
      this.prevButton = root.querySelector('[data-dmp-prev]');
      this.nextButton = root.querySelector('[data-dmp-next]');
      this.shuffleButton = root.querySelector('[data-dmp-shuffle]');
      this.repeatButton = root.querySelector('[data-dmp-repeat]');
      this.repeatLabel = root.querySelector('[data-dmp-repeat-label]');
      this.muteButton = root.querySelector('[data-dmp-mute]');
      this.progress = root.querySelector('[data-dmp-progress]');
      this.queue = root.querySelector('[data-dmp-queue]');
      this.currentTime = root.querySelector('[data-dmp-current]');
      this.duration = root.querySelector('[data-dmp-duration]');
      this.title = root.querySelector('[data-dmp-title]');
      this.artist = root.querySelector('[data-dmp-artist]');
      this.cover = root.querySelector('[data-dmp-cover]');
      this.launcherArt = root.querySelector('[data-dmp-launcher-art]');
      this.ambientImage = root.querySelector('[data-dmp-ambient-image]');
      this.status = root.querySelector('[data-dmp-status]');
      this.viewButtons = [...root.querySelectorAll('[data-dmp-view]')];

      this.index = 0;
      this.repeatMode = 'off';
      this.shuffle = false;
      this.isOpen = false;
      this.isSeeking = false;
      this.sourceIndex = -1;
      this.statusTimer = null;
      this.saveTimer = null;
      this.lastFocused = null;
      this.state = this.readState();
      this.view = 'now';

      this.onDocumentKeydown = this.onDocumentKeydown.bind(this);
      this.onPageHide = this.onPageHide.bind(this);
      this.boundQueueClick = (event) => {
        const item = event.target.closest('[data-track-index]');
        if (!item) return;
        this.select(Number(item.dataset.trackIndex), true);
        this.setView('now');
      };

      this.init();
    }

    readData() {
      const node = this.root.querySelector('[data-dmp-data]');
      if (!node) return { settings: {}, tracks: [] };
      try { return JSON.parse(node.textContent); }
      catch (error) { console.warn('[DECO Music] Invalid player data.', error); return { settings: {}, tracks: [] }; }
    }

    readState() {
      if (!this.settings?.remember) return null;
      try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null'); }
      catch { return null; }
    }

    init() {
      if (!this.audio || !this.panel || !this.scrim) return;
      if (!this.tracks.length) {
        this.root.classList.add('dmp-empty');
        this.toggleButtons.forEach((button) => { button.disabled = true; button.setAttribute('aria-disabled', 'true'); });
        return;
      }
      const restoredIndex = Number(this.state?.index);
      this.index = Number.isInteger(restoredIndex) ? clamp(restoredIndex, 0, this.tracks.length - 1) : 0;
      this.repeatMode = ['off','all','one'].includes(this.state?.repeatMode) ? this.state.repeatMode : 'off';
      this.shuffle = Boolean(this.state?.shuffle);
      this.audio.volume = Number.isFinite(Number(this.state?.volume)) ? clamp(Number(this.state.volume), 0, 1) : this.settings.defaultVolume;
      this.audio.muted = Boolean(this.state?.muted);
      this.updateModeUI();
      this.renderQueue();
      this.paintTrack(this.index);
      this.bindEvents();
      this.setView('now');
      if (this.state?.wasPlaying) this.restorePlayback();
    }

    bindEvents() {
      this.openButton?.addEventListener('click', () => this.open());
      this.closeButton?.addEventListener('click', () => this.close());
      this.scrim?.addEventListener('click', () => this.close());
      this.toggleButtons.forEach((button) => button.addEventListener('click', (event) => { event.stopPropagation(); this.toggle(); }));
      this.viewButtons.forEach((button) => button.addEventListener('click', () => this.setView(button.getAttribute('data-dmp-view') || 'now')));
      this.prevButton?.addEventListener('click', () => this.previous());
      this.nextButton?.addEventListener('click', () => this.next(true));
      this.shuffleButton?.addEventListener('click', () => this.toggleShuffle());
      this.repeatButton?.addEventListener('click', () => this.cycleRepeat());
      this.muteButton?.addEventListener('click', () => this.toggleMute());
      this.progress?.addEventListener('pointerdown', () => { this.isSeeking = true; });
      this.progress?.addEventListener('input', () => {
        if (!Number.isFinite(this.audio.duration)) return;
        const ratio = Number(this.progress.value) / 1000;
        this.currentTime && (this.currentTime.textContent = formatTime(this.audio.duration * ratio));
        this.updateRangeFill(this.progress, ratio * 100);
      });
      this.progress?.addEventListener('change', () => {
        if (Number.isFinite(this.audio.duration)) this.audio.currentTime = this.audio.duration * (Number(this.progress.value) / 1000);
        this.isSeeking = false;
        this.saveState();
      });
      this.audio.addEventListener('play', () => { this.root.classList.add('dmp-is-playing'); this.updatePlayLabels(true); this.updateMediaSessionPlaybackState('playing'); this.saveState(); });
      this.audio.addEventListener('pause', () => { this.root.classList.remove('dmp-is-playing'); this.updatePlayLabels(false); this.updateMediaSessionPlaybackState('paused'); this.saveState(); });
      this.audio.addEventListener('loadedmetadata', () => {
        const restoreTime = Number(this.pendingTime);
        if (Number.isFinite(restoreTime) && restoreTime > 0) this.audio.currentTime = Math.min(restoreTime, Math.max(0, this.audio.duration - 0.25));
        this.pendingTime = null;
        this.duration && (this.duration.textContent = formatTime(this.audio.duration));
        this.updateTimeline();
      });
      this.audio.addEventListener('durationchange', () => { this.duration && (this.duration.textContent = formatTime(this.audio.duration)); });
      this.audio.addEventListener('timeupdate', () => { this.updateTimeline(); this.scheduleSave(); this.updateMediaSessionPosition(); });
      this.audio.addEventListener('ended', () => {
        if (this.repeatMode === 'one') { this.audio.currentTime = 0; this.play(); return; }
        if (this.settings.autoplayNext || this.repeatMode === 'all') this.next(false);
        else { this.root.classList.remove('dmp-is-playing'); this.updatePlayLabels(false); this.saveState(); }
      });
      this.audio.addEventListener('error', () => { this.notify('This audio file could not be loaded.'); this.root.classList.remove('dmp-is-playing'); this.updatePlayLabels(false); });
      document.addEventListener('keydown', this.onDocumentKeydown);
      window.addEventListener('pagehide', this.onPageHide, { capture: true });
    }

    renderQueue() {
      this.queue.innerHTML = this.tracks.map((track, index) => `
        <button class="dmp-track${index === this.index ? ' dmp-current' : ''}" type="button" data-track-index="${index}" aria-label="Play ${escapeHTML(track.title)} by ${escapeHTML(track.artist)}">
          <span class="dmp-track-art">${track.cover ? `<img src="${escapeHTML(track.cover)}" alt="" width="240" height="148" loading="lazy">` : ''}</span>
          <span class="dmp-track-copy-small">
            <span class="dmp-track-title">${escapeHTML(track.title)}</span>
            <span class="dmp-track-artist">${escapeHTML(track.artist)}</span>
          </span>
          <span class="dmp-track-index">${String(index + 1).padStart(2,'0')}</span>
        </button>
      `).join('');
      this.queue.removeEventListener('click', this.boundQueueClick);
      this.queue.addEventListener('click', this.boundQueueClick);
    }

    paintTrack(index) {
      const track = this.tracks[index];
      if (!track) return;
      this.root.style.setProperty('--dmp-accent', track.accent || '#dfe9c8');
      this.root.style.setProperty('--dmp-accent-2', track.accent2 || track.accent || '#d7ccb6');
      this.title && (this.title.textContent = track.title || 'Untitled');
      this.artist && (this.artist.textContent = track.artist || '');
      [this.cover, this.launcherArt, this.ambientImage].forEach((image) => {
        if (!image) return;
        if (track.cover) { image.src = track.cover; image.hidden = false; }
        else { image.removeAttribute('src'); image.hidden = true; }
      });
      if (this.cover) this.cover.alt = track.cover ? `${track.title} cover artwork` : '';
      this.updateCurrentQueueItem();
      this.updateMediaSessionMetadata(track);
    }

    ensureSource(restoreTime = null) {
      const track = this.tracks[this.index];
      if (!track) return false;
      if (this.sourceIndex !== this.index) {
        this.pendingTime = restoreTime;
        this.audio.src = track.audio;
        this.sourceIndex = this.index;
        this.audio.load();
      } else if (Number.isFinite(Number(restoreTime))) this.audio.currentTime = Number(restoreTime);
      return true;
    }

    async play() { if (!this.ensureSource()) return; try { await this.audio.play(); } catch { this.notify('Tap play once to start the soundtrack.'); } }
    pause() { this.audio.pause(); }
    toggle() { this.audio.paused ? this.play() : this.pause(); }

    select(index, autoplay = true) {
      if (!Number.isInteger(index) || index < 0 || index >= this.tracks.length) return;
      const wasPlaying = !this.audio.paused;
      this.index = index;
      this.audio.pause();
      this.audio.removeAttribute('src');
      this.audio.load();
      this.sourceIndex = -1;
      this.progress && (this.progress.value = '0');
      this.currentTime && (this.currentTime.textContent = '0:00');
      this.duration && (this.duration.textContent = '0:00');
      this.updateRangeFill(this.progress, 0);
      this.paintTrack(index);
      this.saveState();
      this.setView('now');
      if (autoplay || wasPlaying) this.play();
    }

    previous() {
      if (this.audio.currentTime > 4) { this.audio.currentTime = 0; return; }
      const nextIndex = this.index === 0 ? this.tracks.length - 1 : this.index - 1;
      this.select(nextIndex, true);
    }

    next(userInitiated = true) {
      let nextIndex;
      if (this.shuffle && this.tracks.length > 1) {
        do { nextIndex = Math.floor(Math.random() * this.tracks.length); } while (nextIndex === this.index);
      } else {
        nextIndex = this.index + 1;
        if (nextIndex >= this.tracks.length) {
          if (this.repeatMode === 'all' || userInitiated) nextIndex = 0;
          else { this.pause(); this.audio.currentTime = 0; return; }
        }
      }
      this.select(nextIndex, true);
    }

    toggleShuffle() { this.shuffle = !this.shuffle; this.updateModeUI(); this.notify(this.shuffle ? 'Shuffle on' : 'Shuffle off'); this.saveState(); }
    cycleRepeat() { this.repeatMode = this.repeatMode === 'off' ? 'all' : this.repeatMode === 'all' ? 'one' : 'off'; this.updateModeUI(); this.notify(this.repeatMode === 'one' ? 'Repeat one' : this.repeatMode === 'all' ? 'Repeat all' : 'Repeat off'); this.saveState(); }
    toggleMute() { this.audio.muted = !this.audio.muted; this.updateModeUI(); this.saveState(); }

    setView(view) {
      this.view = view === 'queue' ? 'queue' : 'now';
      this.root.classList.toggle('dmp-view-now', this.view === 'now');
      this.root.classList.toggle('dmp-view-queue', this.view === 'queue');
      this.viewButtons.forEach((button) => {
        const active = (button.getAttribute('data-dmp-view') || 'now') === this.view;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', String(active));
      });
    }

    updateModeUI() {
      this.root.classList.toggle('dmp-is-muted', this.audio.muted || this.audio.volume === 0);
      this.shuffleButton?.classList.toggle('dmp-active', this.shuffle);
      this.repeatButton?.classList.toggle('dmp-active', this.repeatMode !== 'off');
      this.repeatButton?.setAttribute('aria-label', this.repeatMode === 'one' ? 'Repeat one' : this.repeatMode === 'all' ? 'Repeat all' : 'Repeat off');
      if (this.repeatLabel) this.repeatLabel.textContent = this.repeatMode === 'one' ? 'Repeat 1' : this.repeatMode === 'all' ? 'Repeat All' : 'Repeat';
    }

    updatePlayLabels(isPlaying) { this.toggleButtons.forEach((button) => button.setAttribute('aria-label', isPlaying ? 'Pause' : 'Play')); }

    updateTimeline() {
      if (this.isSeeking || !this.progress) return;
      const duration = this.audio.duration, current = this.audio.currentTime;
      if (!Number.isFinite(duration) || duration <= 0) { this.progress.value = '0'; this.currentTime && (this.currentTime.textContent = '0:00'); this.updateRangeFill(this.progress, 0); return; }
      const ratio = clamp(current / duration, 0, 1);
      this.progress.value = String(Math.round(ratio * 1000));
      this.currentTime && (this.currentTime.textContent = formatTime(current));
      this.duration && (this.duration.textContent = formatTime(duration));
      this.updateRangeFill(this.progress, ratio * 100);
    }

    updateRangeFill(input, percentage) { input?.style.setProperty('--dmp-value', `${clamp(percentage, 0, 100)}%`); }

    updateCurrentQueueItem() {
      this.queue?.querySelectorAll('[data-track-index]').forEach((item) => {
        const isCurrent = Number(item.dataset.trackIndex) === this.index;
        item.classList.toggle('dmp-current', isCurrent);
        item.setAttribute('aria-current', isCurrent ? 'true' : 'false');
      });
    }

    open() {
      if (this.isOpen) return;
      this.isOpen = true;
      this.lastFocused = document.activeElement;
      this.scrim.hidden = false;
      requestAnimationFrame(() => this.scrim.classList.add('dmp-visible'));
      this.panel.classList.add('dmp-open');
      this.panel.setAttribute('aria-hidden', 'false');
      this.openButton?.setAttribute('aria-expanded', 'true');
      document.body.classList.add('dmp-lock');
      this.closeButton?.focus({ preventScroll: true });
    }

    close() {
      if (!this.isOpen) return;
      this.isOpen = false;
      this.scrim.classList.remove('dmp-visible');
      this.panel.classList.remove('dmp-open');
      this.panel.setAttribute('aria-hidden', 'true');
      this.openButton?.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('dmp-lock');
      window.setTimeout(() => { if (!this.isOpen) this.scrim.hidden = true; }, 340);
      this.lastFocused?.focus?.({ preventScroll: true });
    }

    onDocumentKeydown(event) {
      const target = event.target;
      const isTyping = target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement || target?.isContentEditable;
      if (event.key === 'Escape' && this.isOpen) { event.preventDefault(); this.close(); return; }
      if (this.isOpen && event.key === 'Tab') { this.trapFocus(event); return; }
      if (isTyping || event.ctrlKey || event.metaKey || event.altKey) return;
      if (event.code === 'Space' && this.isOpen) { if (target instanceof HTMLButtonElement) return; event.preventDefault(); this.toggle(); }
      else if (event.key.toLowerCase() === 'm' && this.isOpen) { event.preventDefault(); this.toggleMute(); }
      else if (event.key === 'ArrowRight' && this.isOpen) { event.preventDefault(); if (this.ensureSource()) this.audio.currentTime = Math.min(this.audio.duration || Infinity, this.audio.currentTime + 10); }
      else if (event.key === 'ArrowLeft' && this.isOpen) { event.preventDefault(); if (this.ensureSource()) this.audio.currentTime = Math.max(0, this.audio.currentTime - 10); }
    }

    trapFocus(event) {
      const elements = [...this.panel.querySelectorAll('button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])')].filter((element) => !element.hidden && element.offsetParent !== null);
      if (!elements.length) return;
      const first = elements[0], last = elements[elements.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    }

    restorePlayback() {
      const restoreTime = Number(this.state?.currentTime || 0);
      this.ensureSource(restoreTime);
      const attempt = () => { this.audio.play().catch(() => { this.notify('Tap play to resume.'); }); };
      if (this.audio.readyState >= 1) attempt(); else this.audio.addEventListener('loadedmetadata', attempt, { once: true });
    }

    scheduleSave() {
      if (!this.settings.remember || this.saveTimer) return;
      this.saveTimer = window.setTimeout(() => { this.saveTimer = null; this.saveState(); }, 1500);
    }

    saveState() {
      if (!this.settings.remember) return;
      const payload = { index: this.index, currentTime: Number.isFinite(this.audio.currentTime) ? this.audio.currentTime : 0, volume: this.audio.volume, muted: this.audio.muted, wasPlaying: !this.audio.paused && !this.audio.ended, repeatMode: this.repeatMode, shuffle: this.shuffle, savedAt: Date.now() };
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(payload)); } catch {}
    }

    onPageHide() { this.saveState(); }
    notify(message) { if (!this.status) return; window.clearTimeout(this.statusTimer); this.status.textContent = message; this.statusTimer = window.setTimeout(() => { this.status.textContent = ''; }, 2400); }

    updateMediaSessionMetadata(track) {
      if (!('mediaSession' in navigator) || typeof MediaMetadata === 'undefined') return;
      try {
        navigator.mediaSession.metadata = new MediaMetadata({ title: track.title || 'Untitled', artist: track.artist || 'DECO', album: 'DECO SOUND', artwork: track.cover ? [{ src: track.cover, sizes: '512x512', type: 'image/jpeg' }] : [] });
        navigator.mediaSession.setActionHandler('play', () => this.play());
        navigator.mediaSession.setActionHandler('pause', () => this.pause());
        navigator.mediaSession.setActionHandler('previoustrack', () => this.previous());
        navigator.mediaSession.setActionHandler('nexttrack', () => this.next(true));
        navigator.mediaSession.setActionHandler('seekbackward', (details) => { this.audio.currentTime = Math.max(0, this.audio.currentTime - (details.seekOffset || 10)); });
        navigator.mediaSession.setActionHandler('seekforward', (details) => { this.audio.currentTime = Math.min(this.audio.duration || Infinity, this.audio.currentTime + (details.seekOffset || 10)); });
        navigator.mediaSession.setActionHandler('seekto', (details) => { if (Number.isFinite(details.seekTime)) this.audio.currentTime = details.seekTime; });
      } catch {}
    }
    updateMediaSessionPlaybackState(state) { if (!('mediaSession' in navigator)) return; try { navigator.mediaSession.playbackState = state; } catch {} }
    updateMediaSessionPosition() { if (!('mediaSession' in navigator) || !navigator.mediaSession.setPositionState) return; if (!Number.isFinite(this.audio.duration) || this.audio.duration <= 0) return; try { navigator.mediaSession.setPositionState({ duration: this.audio.duration, playbackRate: this.audio.playbackRate, position: Math.min(this.audio.currentTime, this.audio.duration) }); } catch {} }
    destroy() { document.removeEventListener('keydown', this.onDocumentKeydown); window.removeEventListener('pagehide', this.onPageHide, { capture: true }); this.queue?.removeEventListener('click', this.boundQueueClick); this.audio?.pause(); }
  }

  const instances = new WeakMap();
  const mount = (scope = document) => { scope.querySelectorAll(ROOT_SELECTOR).forEach((root) => { if (instances.has(root) || root.dataset.dmpMounted === 'true') return; root.dataset.dmpMounted = 'true'; instances.set(root, new DecoMusicPlayer(root)); }); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => mount(), { once: true });
  else mount();
  document.addEventListener('shopify:section:load', (event) => mount(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.matches?.(ROOT_SELECTOR) ? event.target : event.target.querySelector?.(ROOT_SELECTOR);
    instances.get(root)?.destroy?.();
    if (root) { instances.delete(root); delete root.dataset.dmpMounted; }
  });
})();